import { rawApi as api } from '../utils/apiWithLogging';

// Types
export enum CancellationReasonCategory {
  PRICE = 'PRICE',
  SERVICE = 'SERVICE',
  NO_NEED = 'NO_NEED',
  COMPETITOR = 'COMPETITOR',
  FINANCIAL = 'FINANCIAL',
  CONTRACT_END = 'CONTRACT_END',
  OTHER = 'OTHER'
}

export enum NotificationChannel {
  PHONE = 'PHONE',
  EMAIL = 'EMAIL',
  LINE = 'LINE',
  MEETING = 'MEETING',
  OTHER = 'OTHER'
}

export enum CancellationNoticeStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  RETAINED = 'RETAINED',
  CANCELLED = 'CANCELLED'
}

export enum ContactMethod {
  PHONE = 'PHONE',
  EMAIL = 'EMAIL',
  LINE = 'LINE',
  VISIT = 'VISIT',
  OTHER = 'OTHER'
}

export enum FollowUpResult {
  POSITIVE = 'POSITIVE',
  NEGATIVE = 'NEGATIVE',
  NEUTRAL = 'NEUTRAL',
  PENDING = 'PENDING'
}

export interface RentalCancellationNotice {
  id: number;
  rental_id: number;
  notice_date: string;
  planned_cancellation_date: string;
  reason?: string;
  reason_category: CancellationReasonCategory;
  notified_by?: string;  // deprecated - ใช้ notified_by_user_id แทน
  notified_by_user_id?: number;  // FK ไปยัง User
  notified_channel: NotificationChannel;
  status: CancellationNoticeStatus;
  retained_conditions?: string;
  final_outcome?: string;
  created_by_user_id: number;
  assigned_to_user_id?: number;
  created_at: string;
  updated_at?: string;

  // Extended fields
  rental_code?: string;
  company_name?: string;
  site_name?: string;
  created_by_username?: string;
  created_by_fullname?: string;
  assigned_to_username?: string;
  assigned_to_fullname?: string;
  notified_by_username?: string;
  notified_by_fullname?: string;
  // ชื่อเต็มที่ backend ส่งจริง (schemas/rental_cancellation_notice.py ใช้ *_full_name — ช่อง *_fullname ข้างบนไม่เคยมีค่า)
  created_by_full_name?: string | null;
  assigned_to_full_name?: string | null;
  notified_by_full_name?: string | null;
  follow_ups_count: number;
  last_follow_up_date?: string;
  last_follow_up_result?: string;
}

export interface RentalCancellationNoticeCreate {
  rental_id: number;
  notice_date: string;
  planned_cancellation_date: string;
  reason?: string;
  reason_category: CancellationReasonCategory;
  notified_by?: string;  // deprecated - ใช้ notified_by_user_id แทน
  notified_by_user_id?: number;  // FK ไปยัง User
  notified_channel: NotificationChannel;
  assigned_to_user_id?: number;
  create_return_request?: boolean;  // Option to auto-create Equipment Return Request
}

export interface RentalCancellationNoticeUpdate {
  status?: CancellationNoticeStatus;
  // เลื่อนวันที่จะยกเลิก (feedback บัญชี 21 ก.ย. 2026) — backend เลื่อน billing_end_date ของสัญญา
  // + expected_return_date ของใบตามเก็บที่ยังเปิดอยู่ให้ · ส่ง key เฉพาะเมื่อผู้ใช้แก้ค่าจริง
  planned_cancellation_date?: string;
  retained_conditions?: string;
  final_outcome?: string;
  assigned_to_user_id?: number;
}

export interface RentalFollowUp {
  id: number;
  cancellation_notice_id: number;
  follow_up_date: string;
  follow_up_by_user_id: number;
  action_taken: string;
  contact_method: ContactMethod;
  response_received: boolean;
  customer_feedback?: string;
  result: FollowUpResult;
  next_action?: string;
  next_follow_up_date?: string;
  notes?: string;
  created_at: string;
  updated_at?: string;
  
  // Extended fields
  follow_up_by_username?: string;
  follow_up_by_fullname?: string;
  rental_code?: string;
  company_name?: string;
  site_name?: string;
}

export interface RentalFollowUpCreate {
  cancellation_notice_id: number;
  follow_up_date: string;
  action_taken: string;
  contact_method: ContactMethod;
  response_received: boolean;
  customer_feedback?: string;
  result: FollowUpResult;
  next_action?: string;
  next_follow_up_date?: string;
  notes?: string;
}

export interface RentalFollowUpUpdate {
  action_taken?: string;
  contact_method?: ContactMethod;
  response_received?: boolean;
  customer_feedback?: string;
  result?: FollowUpResult;
  next_action?: string;
  next_follow_up_date?: string;
  notes?: string;
}

export interface CancellationDashboardStats {
  total_notices: number;
  pending_notices: number;
  in_progress_notices: number;
  retained_rentals: number;
  cancelled_rentals: number;
  by_reason: Record<string, number>;
  by_channel: Record<string, number>;
  retention_rate: number;
  notices_this_month: number;
  notices_last_month: number;
  average_resolution_days: number;
}

export interface FollowUpSummary {
  total_follow_ups: number;
  positive_results: number;
  negative_results: number;
  neutral_results: number;
  pending_results: number;
  by_contact_method: Record<string, number>;
  response_rate: number;
  avg_follow_ups_per_notice: number;
}

export interface GetCancellationNoticesParams {
  skip?: number;
  limit?: number;
  status?: CancellationNoticeStatus;
  assigned_to_user_id?: number;
  from_date?: string;
  to_date?: string;
  search?: string;
}

// ---------------------------------------------------------------------------------------------
// เปิดใบแจ้งยกเลิกใหม่ — ย้อน "ยกเลิกจริง" ที่กดก่อนกำหนด (ทาง ค · feedback amintra 21 ก.ย. 2026)
// GET /rentals/cancellation-notices/{id}/reopen-status · POST …/reopen — ดู services/cancellation_reopen.py
// ---------------------------------------------------------------------------------------------
export interface ReopenPiece {
  kind: 'device' | 'sim' | 'stock';
  label: string;
  status: string;
  status_label: string;
  quantity: number;
  note: string;
}

export interface DepositToReinstate {
  count: number;
  amount: number;
}

export interface CancellationReopenStatus {
  notice_id: number;
  notice_status: string;
  rental_id: number;
  rental_number: string | null;
  rental_status: string | null;
  eligible: boolean;
  // ELIGIBLE · NOTICE_NOT_CANCELLED · RENTAL_DELETED · RENTAL_NOT_CANCELLED · OTHER_OPEN_NOTICE
  // · GOODS_HELD_ELSEWHERE · STOCK_INSUFFICIENT · DEPOSIT_ALREADY_RETURNED
  reason_code: string;
  message: string;
  planned_cancellation_date: string | null;
  billing_end_date: string | null;
  planned_date_in_past: boolean;
  needs_new_planned_date: boolean; // true = ต้องส่ง new_planned_cancellation_date
  items: ReopenPiece[];
  blockers: string[];
  deposits_to_reinstate: DepositToReinstate;
  deposits_returned: string[];
  warnings: string[];
  can_reopen: boolean;
  /** สถานะที่ใบแจ้งจะกลับไปหลังเปิดใหม่ — IN_PROGRESS เมื่อเคยมีบันทึกติดตาม (BUG-S2237-001) */
  notice_status_after?: 'PENDING' | 'IN_PROGRESS';
  blocked_reason: string | null;
}

export interface CancellationReopenRequest {
  reason: string; // ≥ 5 ตัวอักษร
  new_planned_cancellation_date?: string; // YYYY-MM-DD
}

export interface CancellationReopenResult {
  notice_id: number;
  notice_status: string;
  rental_id: number;
  rental_number: string | null;
  rental_status: string;
  planned_cancellation_date: string | null;
  billing_end_date: string | null;
  deposits_reinstated: DepositToReinstate;
  return_items_rescheduled: number;
  items: ReopenPiece[];
  warnings: string[];
  message: string;
}

// API Functions
const rentalCancellationService = {
  // Cancellation Notices
  createCancellationNotice: async (data: RentalCancellationNoticeCreate) => {
    const response = await api.post('/rentals/cancellation-notices', data);
    return response.data;
  },

  getCancellationNotices: async (params?: GetCancellationNoticesParams) => {
    const response = await api.get('/rentals/cancellation-notices', { params });
    return response.data;
  },

  getCancellationNotice: async (id: number) => {
    const response = await api.get(`/rentals/cancellation-notices/${id}`);
    return response.data;
  },

  updateCancellationNotice: async (id: number, data: RentalCancellationNoticeUpdate) => {
    const response = await api.put(`/rentals/cancellation-notices/${id}`, data);
    return response.data;
  },

  // ทาง ค: เปิดใบแจ้งยกเลิกใหม่ได้ไหม — backend ตอบ 200 เสมอ (eligible=false + reason_code เมื่อยังไม่ได้)
  getReopenStatus: async (id: number): Promise<CancellationReopenStatus> => {
    const response = await api.get(`/rentals/cancellation-notices/${id}/reopen-status`);
    return response.data;
  },

  // ทาง ค: ย้อน "ยกเลิกจริง" — ใบแจ้ง → รอดำเนินการ · สัญญา → รอการยกเลิก · มัดจำรอคืน → ชำระแล้ว
  // ส่ง new_planned_cancellation_date เพื่อเลื่อนวันยกเลิกในคำขอเดียว (บังคับเมื่อวันเดิมผ่านไปแล้ว)
  reopenCancellationNotice: async (id: number, data: CancellationReopenRequest): Promise<CancellationReopenResult> => {
    const response = await api.post(`/rentals/cancellation-notices/${id}/reopen`, data);
    return response.data;
  },

  getRentalCancellationHistory: async (rentalId: number) => {
    const response = await api.get(`/rentals/cancellation-notices/rental/${rentalId}`);
    return response.data;
  },

  getDashboardStats: async (from_date?: string, to_date?: string) => {
    const params = { from_date, to_date };
    const response = await api.get('/rentals/cancellation-notices/dashboard/stats', { params });
    return response.data;
  },

  getPendingCancellations: async (days_ahead: number = 7) => {
    const response = await api.get('/rentals/cancellation-notices/dashboard/pending', { 
      params: { days_ahead } 
    });
    return response.data;
  },

  // Follow-ups
  createFollowUp: async (noticeId: number, data: RentalFollowUpCreate) => {
    const response = await api.post(`/rentals/cancellation-notices/${noticeId}/follow-ups`, data);
    return response.data;
  },

  getNoticeFollowUps: async (noticeId: number) => {
    const response = await api.get(`/rentals/cancellation-notices/${noticeId}/follow-ups`);
    return response.data;
  },

  updateFollowUp: async (id: number, data: RentalFollowUpUpdate) => {
    const response = await api.put(`/rentals/follow-ups/${id}`, data);
    return response.data;
  },

  getUpcomingFollowUps: async (days_ahead: number = 7, assigned_to_me: boolean = false) => {
    const params = { days_ahead, assigned_to_me };
    const response = await api.get('/rentals/follow-ups/upcoming', { params });
    return response.data;
  },

  getFollowUpSummary: async (from_date?: string, to_date?: string, my_follow_ups?: boolean) => {
    const params = { from_date, to_date, my_follow_ups };
    const response = await api.get('/rentals/follow-ups/summary', { params });
    return response.data;
  },

  // Helper functions
  getCancellationReasonLabel: (category: CancellationReasonCategory): string => {
    const labels: Record<CancellationReasonCategory, string> = {
      [CancellationReasonCategory.PRICE]: 'ราคาแพง',
      [CancellationReasonCategory.SERVICE]: 'บริการไม่ดี',
      [CancellationReasonCategory.NO_NEED]: 'ไม่ต้องการใช้แล้ว',
      [CancellationReasonCategory.COMPETITOR]: 'ย้ายไปใช้คู่แข่ง',
      [CancellationReasonCategory.FINANCIAL]: 'ปัญหาการเงิน',
      [CancellationReasonCategory.CONTRACT_END]: 'หมดสัญญากับลูกค้า',
      [CancellationReasonCategory.OTHER]: 'อื่นๆ'
    };
    return labels[category] || category;
  },

  getNotificationChannelLabel: (channel: NotificationChannel): string => {
    const labels: Record<NotificationChannel, string> = {
      [NotificationChannel.PHONE]: 'โทรศัพท์',
      [NotificationChannel.EMAIL]: 'อีเมล',
      [NotificationChannel.LINE]: 'ไลน์',
      [NotificationChannel.MEETING]: 'ประชุม/พบหน้า',
      [NotificationChannel.OTHER]: 'อื่นๆ'
    };
    return labels[channel] || channel;
  },

  getCancellationStatusLabel: (status: CancellationNoticeStatus): string => {
    const labels: Record<CancellationNoticeStatus, string> = {
      [CancellationNoticeStatus.PENDING]: 'รอดำเนินการ',
      [CancellationNoticeStatus.IN_PROGRESS]: 'กำลังติดตาม',
      [CancellationNoticeStatus.RETAINED]: 'รักษาลูกค้าไว้ได้',
      [CancellationNoticeStatus.CANCELLED]: 'ยกเลิกจริง'
    };
    return labels[status] || status;
  },

  getContactMethodLabel: (method: ContactMethod): string => {
    const labels: Record<ContactMethod, string> = {
      [ContactMethod.PHONE]: 'โทรศัพท์',
      [ContactMethod.EMAIL]: 'อีเมล',
      [ContactMethod.LINE]: 'ไลน์',
      [ContactMethod.VISIT]: 'เยี่ยมหน้างาน',
      [ContactMethod.OTHER]: 'อื่นๆ'
    };
    return labels[method] || method;
  },

  getFollowUpResultLabel: (result: FollowUpResult): string => {
    const labels: Record<FollowUpResult, string> = {
      [FollowUpResult.POSITIVE]: 'ผลบวก',
      [FollowUpResult.NEGATIVE]: 'ผลลบ',
      [FollowUpResult.NEUTRAL]: 'กลางๆ',
      [FollowUpResult.PENDING]: 'รอผลตอบกลับ'
    };
    return labels[result] || result;
  }
};

export default rentalCancellationService;